import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, MapPin, Save } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { AddressEditor } from "@/components/AddressEditor";
import { ApplicationsTab } from "@/components/ApplicationsTab";
import { KioskDevicesTab } from "@/components/KioskDevicesTab";
import { KioskAdminPinPanel } from "@/components/KioskAdminPinPanel";
import { InventoryCachePanel } from "@/components/InventoryCachePanel";
import { ScopedCredentialsPanel } from "@/components/ScopedCredentialsPanel";
import { usePageTitle } from "@/lib/use-page-title";
import {
  type AddressValue,
  addressFromAny,
  formatAddressOneLine,
  isAddressValid,
} from "@/lib/address";
import {
  JsonConfigEditor,
  type ConfigValue,
  type SuggestedKey,
} from "@/components/JsonConfigEditor";
import { ConfigurationEditor } from "@/components/ConfigurationEditor";
import { validateEmail, validatePhone } from "@/lib/validation";

// location.configuration is now driven by the schema-aware
// ConfigurationEditor (#170 / V0051 catalog). The legacy curated
// suggestion list lived here; super_admin now declares the
// canonical setting list on /platform/catalog → Settings, and
// the LocationConfigurationTab below renders it directly.

// location.metadata is explicitly NOT read by the POS runtime - it's
// a human-only notebook. Suggest the annotations we've actually seen
// admins reach for so the bag isn't a blank canvas.
const LOCATION_METADATA_SUGGESTED_KEYS: SuggestedKey[] = [
  {
    key: "migrated_from",
    type: "string",
    hint: "Legacy location id this row was imported from.",
  },
  {
    key: "onboarded_by",
    type: "string",
    hint: "Admin who set this location up.",
  },
  {
    key: "onboarded_at",
    type: "string",
    hint: "Go-live date (ISO 8601).",
  },
  { key: "ticket_ref", type: "string", hint: "Linear / Jira ticket driving this change." },
  { key: "notes", type: "string", hint: "Anything else worth remembering." },
];
import { usePartnerScope } from "@/components/partner-scope-context";
import { ProvidersTab } from "@/components/ProvidersTab";
import { TechnicalDetails } from "@/components/TechnicalDetails";
import { apiClient } from "@/lib/api";
import { describe } from "@/lib/api-error";
import { hasAnyOrgTierGrant, hasWriteOnLocation } from "@/lib/roles";
import { ScopedPeopleTab } from "@/components/ScopedPeopleTab";
import { timezoneChoicesIncluding } from "@/lib/timezones";
import { useRoleProfile } from "@/lib/use-role-profile";
import { cn } from "@/lib/utils";
import { Toggle } from "@/components/Toggle";
import { ApiError, type Location } from "@tilt/admin-sdk";

/**
 * /locations/:id - drill-down for a single location.
 *
 * Tabs:
 *   - Overview: editable contact/vertical/status/tax fields.
 *   - Configuration: location.configuration (JSONB bag, shallow-merged).
 *   - Metadata: location.metadata (JSONB bag, shallow-merged).
 *   - Credentials: read-only list of provider_credential_tbl rows
 *     scoped to this location; full CRUD lives at
 *     /provider-credentials.
 *
 * Inheritance note: location.configuration merges with the parent
 * organization's configuration at read time in the POS runtime; the
 * admin UI shows just this row's own overrides.
 */
type TabKey =
  | "overview"
  | "configuration"
  | "metadata"
  | "applications"
  | "providers"
  | "credentials"
  | "users"
  | "kiosks";

export function LocationDetailPage(): JSX.Element {
  usePageTitle("Location");
  const { id = "" } = useParams<{ id: string }>();
  const { scope } = usePartnerScope();
  const [tab, setTab] = useState<TabKey>("overview");
  const profile = useRoleProfile();
  // Providers + Credentials surface payment-provider configuration and
  // encrypted secrets. A location_manager (location-only grant) has no
  // need to see either - wiring lives one tier up. Org/partner/super
  // tier callers keep both tabs. The same predicate is used in the
  // sidebar's Credentials nav gate so the rule lives in one place.
  const canSeeProviders = hasAnyOrgTierGrant(profile);

  const loc = useQuery({
    queryKey: ["locations", "detail", id, scope.id],
    queryFn: () => apiClient.getLocation(id),
    retry: false,
    enabled: !!id,
  });

  if (!id) return <p className="text-sm text-destructive">Missing location id.</p>;
  if (loc.isLoading) {
    return <p className="text-sm text-muted-foreground">Loading location…</p>;
  }
  if (loc.isError || !loc.data) {
    const msg = loc.error instanceof ApiError ? loc.error.message : "Failed to load location.";
    return <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{msg}</div>;
  }

  const data = loc.data;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <Link
            to="/locations"
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3 w-3" /> All locations
          </Link>
          <h1 className="mt-1 flex items-center gap-2 text-xl font-semibold tracking-tight sm:text-2xl">
            <MapPin className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
            {data.name}
          </h1>
          <p className="break-all text-xs text-muted-foreground">
            <span className="font-mono">{data.partner_path}</span>
            {" / "}
            {data.organization_name ? (
              <Link
                to={`/organizations/${data.organization_id}`}
                className="text-primary hover:underline"
              >
                {data.organization_name}
              </Link>
            ) : (
              <span className="font-mono">{data.organization_id}</span>
            )}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-1 border-b">
        <TabButton active={tab === "overview"} onClick={() => setTab("overview")}>
          Overview
        </TabButton>
        <TabButton active={tab === "configuration"} onClick={() => setTab("configuration")}>
          Configuration
        </TabButton>
        {canSeeProviders && (
          <TabButton active={tab === "providers"} onClick={() => setTab("providers")}>
            Providers
          </TabButton>
        )}
        <TabButton active={tab === "metadata"} onClick={() => setTab("metadata")}>
          Admin notes
        </TabButton>
        <TabButton active={tab === "applications"} onClick={() => setTab("applications")}>
          Applications
        </TabButton>
        {canSeeProviders && (
          <TabButton active={tab === "credentials"} onClick={() => setTab("credentials")}>
            Credentials
          </TabButton>
        )}
        <TabButton active={tab === "users"} onClick={() => setTab("users")}>
          People
        </TabButton>
        <TabButton active={tab === "kiosks"} onClick={() => setTab("kiosks")}>
          Kiosks
        </TabButton>
      </div>

      {tab === "overview" && <OverviewTab loc={data} />}
      {tab === "configuration" && <LocationConfigurationTab loc={data} />}
      {tab === "metadata" && (
        <BagEditorTab
          loc={data}
          field="metadata"
          label="Admin notes"
          hint="Human-only annotations - migration notes, ticket references, onboarding audit trail. The POS runtime never reads this bag."
          suggestedKeys={LOCATION_METADATA_SUGGESTED_KEYS}
        />
      )}
      {tab === "applications" && (
        <ApplicationsTab
          scope="location"
          id={data.id}
          readOnly={!hasWriteOnLocation(profile, data.id, data.organization_id, data.partner_path)}
        />
      )}
      {tab === "providers" && canSeeProviders && (
        <ProvidersTab
          scope={{
            kind: "location",
            id: data.id,
            name: data.name,
            partnerPath: data.partner_path,
          }}
        />
      )}
      {tab === "credentials" && canSeeProviders && <CredentialsTab loc={data} />}
      {tab === "kiosks" && (
        <div className="flex flex-col gap-6">
          <KioskDevicesTab
            locationId={data.id}
            canWrite={hasWriteOnLocation(profile, data.id, data.organization_id, data.partner_path)}
          />
          <KioskAdminPinPanel
            locationId={data.id}
            canWrite={hasWriteOnLocation(profile, data.id, data.organization_id, data.partner_path)}
          />
        </div>
      )}
      {tab === "users" && (
        <ScopedPeopleTab
          presetScope={{
            scope_kind: "location",
            scope_id: data.id,
            partner_id: data.partner_id,
            partner_path: data.partner_path,
            label: data.name,
            organization_id: data.organization_id,
          }}
        />
      )}
      <TechnicalDetails label="Technical details">
        <dl className="space-y-1 text-xs text-muted-foreground">
          <DetailRow label="id" value={data.id} mono />
          <DetailRow label="organization_id" value={data.organization_id} mono />
          <DetailRow label="partner_id" value={data.partner_id} mono />
          <DetailRow label="partner_path" value={data.partner_path} mono />
          <DetailRow
            label="vertical"
            value={data.vertical_display_name ?? data.vertical_slug ?? "-"}
          />
          <DetailRow label="status" value={data.status} />
          <DetailRow label="created_at" value={data.created_at} mono />
          {data.is_deleted && <DetailRow label="state" value="soft-deleted" />}
        </dl>
      </TechnicalDetails>
    </div>
  );
}

function TabButton(props: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={props.onClick}
      className={cn(
        "-mb-px border-b-2 px-3 py-1.5 text-sm",
        props.active
          ? "border-primary font-medium text-foreground"
          : "border-transparent text-muted-foreground hover:text-foreground",
      )}
    >
      {props.children}
    </button>
  );
}

function DetailRow(props: { label: string; value: string; mono?: boolean }): JSX.Element {
  return (
    <div className="flex flex-wrap items-baseline gap-2">
      <dt className="w-36 shrink-0 text-[11px] uppercase tracking-wide text-muted-foreground/70">
        {props.label}
      </dt>
      <dd className={cn("min-w-0 break-all", props.mono && "font-mono")}>{props.value}</dd>
    </div>
  );
}

function OverviewTab(props: { loc: Location }): JSX.Element {
  const { loc } = props;
  const queryClient = useQueryClient();
  const profile = useRoleProfile();
  const canWrite = hasWriteOnLocation(profile, loc.id, loc.organization_id, loc.partner_path);
  const [name, setName] = useState(loc.name);
  const [timezone, setTimezone] = useState(loc.timezone);
  const [status, setStatus] = useState<Location["status"]>(loc.status);
  // Empty string in local state = "inherit from org default"; a UUID
  // = an explicit pin. We translate to/from null at PATCH time.
  const [verticalId, setVerticalId] = useState<string>(loc.vertical_id ?? "");
  // Vertical options are scoped to the parent organization's enabled
  // set, not the full platform catalog. The backend already rejects a
  // vertical that isn't enabled on the org; constrain the dropdown so
  // the UI matches what's accepted.
  const orgVerticalsQuery = useQuery({
    queryKey: ["organization-verticals", loc.organization_id],
    queryFn: () => apiClient.listOrganizationVerticals(loc.organization_id),
    enabled: !!loc.organization_id,
    staleTime: 60_000,
  });
  const orgVerticals = orgVerticalsQuery.data?.verticals ?? [];
  // Sales tax is stored server-side as a decimal (0.0875). UI shows
  // and collects it as a percentage (8.75%). Helpers bridge the two
  // on read + write so the user never sees the decimal form.
  const [salesTaxPct, setSalesTaxPct] = useState(decimalToPct(loc.sales_tax_rate));
  const [contactName, setContactName] = useState(loc.contact_name ?? "");
  const [contactEmail, setContactEmail] = useState(loc.contact_email ?? "");
  const [contactPhone, setContactPhone] = useState(loc.contact_phone ?? "");
  const [address, setAddress] = useState<AddressValue>(
    addressFromAny(loc.address, loc.address_json),
  );
  const [logoUrl, setLogoUrl] = useState(loc.logo_url ?? "");
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const emailCheck = validateEmail(contactEmail);
  const phoneCheck = validatePhone(contactPhone);

  useEffect(() => {
    setName(loc.name);
    setTimezone(loc.timezone);
    setStatus(loc.status);
    setVerticalId(loc.vertical_id ?? "");
    setSalesTaxPct(decimalToPct(loc.sales_tax_rate));
    setContactName(loc.contact_name ?? "");
    setContactEmail(loc.contact_email ?? "");
    setContactPhone(loc.contact_phone ?? "");
    setAddress(addressFromAny(loc.address, loc.address_json));
    setLogoUrl(loc.logo_url ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.id, loc.updated_at]);

  const patch = useMutation({
    mutationFn: () => {
      if (!emailCheck.ok) throw new Error(emailCheck.error);
      if (!phoneCheck.ok) throw new Error(phoneCheck.error);
      const anyAddressFilled =
        address.line1.trim() || address.city.trim() || address.state || address.postal_code.trim();
      if (anyAddressFilled && !isAddressValid(address)) {
        throw new Error("Address is incomplete or invalid - check state and postal fields.");
      }
      return apiClient.patchLocation(loc.id, {
        name: name.trim(),
        timezone: timezone.trim(),
        status,
        // "" in local state means "inherit" - send null so the server
        // clears the pin. A UUID becomes an explicit pin.
        vertical_id: verticalId === "" ? null : verticalId,
        sales_tax_rate: pctToDecimal(salesTaxPct),
        contact_name: contactName.trim() || null,
        contact_email: contactEmail.trim() || null,
        contact_phone: contactPhone.trim() || null,
        address: anyAddressFilled ? formatAddressOneLine(address) : null,
        address_json: anyAddressFilled ? address : null,
        logo_url: logoUrl.trim() || null,
      });
    },
    onSuccess: () => {
      setError(null);
      setSaved(true);
      void queryClient.invalidateQueries({ queryKey: ["locations"] });
      setTimeout(() => setSaved(false), 2000);
    },
    onError: (err: unknown) => setError(describe(err)),
  });

  return (
    <div className="space-y-4">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          patch.mutate();
        }}
        className="space-y-3 rounded-lg border bg-card p-4 shadow-sm sm:p-5"
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Name" id="loc-name">
            <input
              id="loc-name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Timezone" id="loc-tz">
            <select
              id="loc-tz"
              required
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            >
              {timezoneChoicesIncluding([loc.timezone]).map((tz) => (
                <option key={tz.value} value={tz.value}>
                  {tz.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Vertical" id="loc-vert">
            <select
              id="loc-vert"
              value={verticalId}
              onChange={(e) => setVerticalId(e.target.value)}
              disabled={orgVerticals.length === 0}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm disabled:opacity-60"
            >
              {/* Empty = inherit. We label it with the org's current
                  default so the admin knows what they'll actually get
                  without having to look it up separately. The snapshot
                  rule (see vertical_memberships._put_location_departments)
                  means this may be converted to a concrete pin later
                  when the admin customises departments. */}
              <option value="">
                Inherit from organization
                {loc.effective_vertical_display_name
                  ? ` (${loc.effective_vertical_display_name})`
                  : ""}
              </option>
              {orgVerticals.map((v) => (
                <option key={v.vertical_id} value={v.vertical_id}>
                  {v.vertical_display_name}
                  {v.is_default ? " (default)" : ""}
                </option>
              ))}
            </select>
            {!orgVerticalsQuery.isLoading && orgVerticals.length === 0 && (
              <p className="mt-1 text-[11px] text-muted-foreground">
                No verticals enabled on this organization. Add at least one on the
                organization&apos;s Verticals tab.
              </p>
            )}
            {loc.inherits_vertical && verticalId === "" && (
              <p className="mt-1 text-[11px] text-muted-foreground">
                This location follows the organization&apos;s default. Customising departments below
                will pin the vertical here so the two stay in sync.
              </p>
            )}
          </Field>
          <Field label="Status" id="loc-status">
            <div className="flex h-[38px] items-center">
              <Toggle
                id="loc-status"
                checked={status === "active"}
                onChange={(on) => setStatus(on ? "active" : "inactive")}
                label={status === "active" ? "Active" : "Inactive"}
                description="Inactive locations can't accept payments."
                onLabel="On"
                offLabel="Off"
              />
            </div>
          </Field>
          <Field label="Sales tax (%)" id="loc-tax">
            <input
              id="loc-tax"
              type="number"
              inputMode="decimal"
              min="0"
              max="99.99"
              step="0.01"
              placeholder="8.25"
              value={salesTaxPct}
              onChange={(e) => setSalesTaxPct(e.target.value)}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Logo URL" id="loc-logo">
            <input
              id="loc-logo"
              type="url"
              value={logoUrl}
              onChange={(e) => setLogoUrl(e.target.value)}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Contact name" id="loc-cname">
            <input
              id="loc-cname"
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Contact email" id="loc-cemail">
            <input
              id="loc-cemail"
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              aria-invalid={!emailCheck.ok}
              className={cn(
                "w-full rounded-md border bg-background px-3 py-2 text-sm",
                !emailCheck.ok && "border-destructive",
              )}
            />
            {!emailCheck.ok && (
              <p className="mt-1 text-[11px] text-destructive">{emailCheck.error}</p>
            )}
          </Field>
          <Field label="Contact phone" id="loc-cphone">
            <input
              id="loc-cphone"
              type="tel"
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
              aria-invalid={!phoneCheck.ok}
              className={cn(
                "w-full rounded-md border bg-background px-3 py-2 text-sm",
                !phoneCheck.ok && "border-destructive",
              )}
            />
            {!phoneCheck.ok && (
              <p className="mt-1 text-[11px] text-destructive">{phoneCheck.error}</p>
            )}
          </Field>
        </div>
        <div>
          <div className="mb-1 text-xs font-medium text-muted-foreground">Address</div>
          <AddressEditor
            idPrefix="loc-addr"
            value={address}
            onChange={setAddress}
            required={false}
          />
        </div>

        {error && (
          <div className="rounded-md bg-destructive/10 p-2 text-xs text-destructive">{error}</div>
        )}
        {canWrite ? (
          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={patch.isPending}
              className="flex items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              <Save className="h-4 w-4" /> {patch.isPending ? "Saving…" : "Save"}
            </button>
            {saved && <span className="text-xs text-primary">Saved.</span>}
          </div>
        ) : (
          <p className="text-[11px] text-muted-foreground">
            Read-only - you don't have write access on{" "}
            <span className="font-mono">{loc.partner_path}</span>.
          </p>
        )}
      </form>
      <DepartmentsCard
        locationId={loc.id}
        organizationId={loc.organization_id}
        verticalId={loc.vertical_id}
        inheritedVerticalId={loc.effective_vertical_id}
        canWrite={canWrite}
      />
      <InventoryCachePanel locationId={loc.id} locationName={loc.name} canWrite={canWrite} />
    </div>
  );
}

/**
 * Per-location department subset. The server returns the currently
 * effective list + its source (location override / org-curated subset
 * / vertical catalog). Candidates for checkboxes come from the org's
 * curated subset if one is set for this vertical, otherwise the full
 * vertical catalog. PUT with [] clears any location-level override
 * and reverts to inheriting the org or catalog list.
 *
 * Uses `loc.vertical_id` - the SAVED vertical - as the source of
 * truth. Changing the vertical in the form above requires saving
 * first before the dept candidates refresh here.
 */
function DepartmentsCard(props: {
  locationId: string;
  organizationId: string;
  // May be NULL when the location is inheriting its vertical from
  // the org (new default post-push-9). We fall back to the org's
  // default vertical so the dept candidates list is still populated.
  verticalId: string | null;
  inheritedVerticalId: string | null;
  /** Mirrors partners.py gate - hide save + reset buttons + disable
   *  checkboxes when the caller can't write on this partner. */
  canWrite: boolean;
}): JSX.Element {
  const queryClient = useQueryClient();
  const orgVerticalsQuery = useQuery({
    queryKey: ["organization-verticals", props.organizationId],
    queryFn: () => apiClient.listOrganizationVerticals(props.organizationId),
    enabled: !!props.organizationId,
    staleTime: 60_000,
  });
  const catalogQuery = useQuery({
    queryKey: ["verticals"],
    queryFn: () => apiClient.listVerticals(),
    staleTime: 60_000,
  });
  const deptsQuery = useQuery({
    queryKey: ["location-departments", props.locationId],
    queryFn: () => apiClient.listLocationDepartments(props.locationId),
  });

  // Resolve "which vertical drives the candidate dept list?" - the
  // location's own pin if set, else the inherited org default.
  // Without this fallback a freshly-created (inheriting) location
  // would show an empty dept list until the admin explicitly picked
  // a vertical, which is worse UX than inheriting silently.
  const effectiveVerticalId = props.verticalId ?? props.inheritedVerticalId;
  const orgVertical = (orgVerticalsQuery.data?.verticals ?? []).find(
    (v) => v.vertical_id === effectiveVerticalId,
  );
  const catalogVertical = (catalogQuery.data?.verticals ?? []).find(
    (v) => v.id === effectiveVerticalId,
  );
  // Candidates = curated subset at org level if any, else full catalog.
  const candidates = useMemo(() => {
    if (orgVertical && orgVertical.departments.length > 0) return orgVertical.departments;
    return catalogVertical?.departments ?? [];
  }, [orgVertical, catalogVertical]);

  const effective = deptsQuery.data?.effective ?? [];
  const source = deptsQuery.data?.source ?? "vertical_catalog";

  const [pending, setPending] = useState<Set<string>>(new Set());
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setPending(new Set(effective.map((d) => d.id)));
    setDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deptsQuery.dataUpdatedAt]);

  const save = useMutation({
    mutationFn: () => apiClient.putLocationDepartments(props.locationId, Array.from(pending)),
    onSuccess: () => {
      setError(null);
      setSaved(true);
      void queryClient.invalidateQueries({ queryKey: ["location-departments", props.locationId] });
      setTimeout(() => setSaved(false), 2000);
    },
    onError: (err: unknown) => setError(describe(err)),
  });
  const clearOverride = useMutation({
    mutationFn: () => apiClient.putLocationDepartments(props.locationId, []),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ["location-departments", props.locationId] });
    },
    onError: (err: unknown) => setError(describe(err)),
  });

  const sourceHint =
    source === "location_override"
      ? `Using a location-specific subset (${effective.length} department${
          effective.length === 1 ? "" : "s"
        }).`
      : source === "org_vertical"
        ? `Inheriting the organization's curated subset (${effective.length}).`
        : `Inheriting the full vertical catalog (${effective.length}).`;

  return (
    <div className="rounded-lg border bg-card p-4 shadow-sm sm:p-5">
      <div className="mb-1 flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold">Departments at this location</h2>
        {source === "location_override" && props.canWrite && (
          <button
            type="button"
            onClick={() => clearOverride.mutate()}
            disabled={clearOverride.isPending}
            className="text-[11px] text-muted-foreground underline hover:text-foreground"
          >
            Reset to inherited
          </button>
        )}
      </div>
      <p className="mb-3 text-xs text-muted-foreground">{sourceHint}</p>
      {candidates.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          This vertical has no departments in the catalog yet. Add departments on the
          /platform/catalog page before curating them here.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
            {candidates.map((d) => (
              <label key={d.id} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={pending.has(d.id)}
                  disabled={!props.canWrite}
                  onChange={(e) => {
                    const next = new Set(pending);
                    if (e.target.checked) next.add(d.id);
                    else next.delete(d.id);
                    setPending(next);
                    setDirty(true);
                  }}
                  className="h-4 w-4 accent-primary disabled:opacity-60"
                />
                <span>{d.display_name}</span>
              </label>
            ))}
          </div>
          {error && (
            <div className="mt-3 rounded-md bg-destructive/10 p-2 text-xs text-destructive">
              {error}
            </div>
          )}
          {props.canWrite && (
            <div className="mt-3 flex items-center gap-3">
              <button
                type="button"
                disabled={!dirty || save.isPending}
                onClick={() => save.mutate()}
                className="flex items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
              >
                <Save className="h-4 w-4" /> {save.isPending ? "Saving…" : "Save departments"}
              </button>
              {saved && <span className="text-xs text-primary">Saved.</span>}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Field(props: { label: string; id: string; children: React.ReactNode }): JSX.Element {
  return (
    <div>
      <label htmlFor={props.id} className="mb-1 block text-xs font-medium text-muted-foreground">
        {props.label}
      </label>
      {props.children}
    </div>
  );
}

/**
 * Location configuration tab - schema-driven editor (#170, V0051
 * catalog). Renders one input control per
 * ``configuration_setting_catalog_tbl`` row whose
 * ``applicable_scopes`` includes ``location``. Replaces the prior
 * free-form JsonConfigEditor; the legacy ``BagEditorTab`` below
 * still drives the Admin Notes (metadata) tab where free-form
 * is the right shape.
 */
function LocationConfigurationTab(props: { loc: Location }): JSX.Element {
  const { loc } = props;
  const queryClient = useQueryClient();
  const profile = useRoleProfile();
  const canWrite = hasWriteOnLocation(profile, loc.id, loc.organization_id, loc.partner_path);
  const current = (loc.configuration ?? {}) as Record<string, unknown>;
  return (
    <div className="space-y-3">
      <ConfigurationEditor
        tier="location"
        value={current}
        canWrite={canWrite}
        onSave={async (next) => {
          await apiClient.patchLocation(loc.id, { configuration: next });
          await queryClient.invalidateQueries({ queryKey: ["locations"] });
          await queryClient.invalidateQueries({ queryKey: ["location", loc.id] });
        }}
      />
      {!canWrite && (
        <p className="text-[11px] text-muted-foreground">
          Read-only - you don&apos;t have write access on{" "}
          <span className="font-mono">{loc.partner_path}</span>.
        </p>
      )}
    </div>
  );
}

function BagEditorTab(props: {
  loc: Location;
  field: "configuration" | "metadata";
  label: string;
  hint: string;
  suggestedKeys?: SuggestedKey[];
}): JSX.Element {
  const { loc, field, label, hint, suggestedKeys } = props;
  const queryClient = useQueryClient();
  const profile = useRoleProfile();
  const canWrite = hasWriteOnLocation(profile, loc.id, loc.organization_id, loc.partner_path);
  const initial = (loc[field] as ConfigValue | undefined) ?? {};
  const [value, setValue] = useState<ConfigValue>(initial);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setValue((loc[field] as ConfigValue | undefined) ?? {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.id, loc.updated_at, field]);

  const patch = useMutation({
    mutationFn: () => apiClient.patchLocation(loc.id, { [field]: value }),
    onSuccess: () => {
      setError(null);
      setSaved(true);
      void queryClient.invalidateQueries({ queryKey: ["locations"] });
      setTimeout(() => setSaved(false), 2000);
    },
    onError: (err: unknown) => setError(describe(err)),
  });

  return (
    <div className="space-y-3 rounded-lg border bg-card p-4 shadow-sm sm:p-5">
      <JsonConfigEditor
        label={label}
        hint={hint}
        {...(suggestedKeys ? { suggestedKeys } : {})}
        value={value}
        onChange={setValue}
      />
      {error && (
        <div className="rounded-md bg-destructive/10 p-2 text-xs text-destructive">{error}</div>
      )}
      {canWrite && (
        <div className="flex items-center gap-3 pt-1">
          <button
            type="button"
            onClick={() => patch.mutate()}
            disabled={patch.isPending}
            className="flex items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            <Save className="h-4 w-4" /> {patch.isPending ? "Saving…" : `Save ${field}`}
          </button>
          {saved && <span className="text-xs text-primary">Saved.</span>}
        </div>
      )}
    </div>
  );
}

function CredentialsTab(props: { loc: Location }): JSX.Element {
  // Push 10: show the *effective* credential set (inherited + own),
  // not just ``location_id = THIS`` rows. Previously an org-scoped
  // credential pinned by a location binding rendered as "No
  // credentials attached to this location", which was technically
  // true but misleading - the admin had no way to find the cred
  // from the location detail. ScopedCredentialsPanel handles the
  // grouping so Partner / Organization detail pages can share it.
  return (
    <ScopedCredentialsPanel
      partnerId={props.loc.partner_id}
      partnerName={props.loc.partner_name ?? "partner"}
      organizationId={props.loc.organization_id}
      organizationName={props.loc.organization_name ?? undefined}
      locationId={props.loc.id}
      locationName={props.loc.name}
    />
  );
}

// Server stores sales_tax_rate as a decimal fraction (0.0875 = 8.75%).
// UI always shows + collects percent - these helpers bridge. Null /
// empty inputs round-trip unchanged so "no tax set" stays "no tax set".
function decimalToPct(decimalRate: string | null | undefined): string {
  if (decimalRate == null || decimalRate === "") return "";
  const n = Number(decimalRate);
  if (!Number.isFinite(n)) return "";
  // Multiply and fix drift (0.0875 * 100 = 8.750000000000001).
  return (Math.round(n * 10000) / 100).toString();
}

function pctToDecimal(pct: string | null | undefined): string | null {
  if (pct == null || String(pct).trim() === "") return null;
  const n = Number(pct);
  if (!Number.isFinite(n)) return null;
  // Keep 4 decimals - enough for 0.0001 precision on the decimal side.
  return (Math.round((n / 100) * 10000) / 10000).toFixed(4);
}
